import {
  ReactElement,
  startTransition,
  Suspense,
  useMemo,
  useOptimistic,
  useState,
} from "react";
import { useToast } from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { accessTokenInfoAtom } from "../store";
import {
  PHRASE_SETTING_KEY,
  phraseSettingQueryAtom,
  RECORDING_PHRASES_KEY,
} from "../stores/phraseStore";
import { LoadingOverlay } from "../components/atoms/LoadingOverlay";
import { SimpleAlertDialog } from "../components/atoms/SimpleAlertDialog";
import { PhraseSettingLayout } from "../components/molecules/PhraseSetting/PhraseSettingLayout";
import { PhraseSettingList } from "../components/molecules/PhraseSetting/PhraseSettingList";
import {
  MinimumUtterancesEditor,
  PhraseSettingEditor,
} from "../components/molecules/PhraseSetting/PhraseSettingEditor";
import {
  DEFAULT_PHRASE_KEYS,
  EMPTY_PHRASES_SETTING,
  createSettingId,
  PhraseSettingFormData,
  toFormData,
  toSettingData,
} from "../components/molecules/PhraseSetting/phraseSettingForm";
import { voislogApi } from "../utils/apiClients/voislogApi";
import { useToastMessage } from "../hooks/useToastMessage";
import { useModifiedTranslation } from "../hooks/useModifiedTranslation";

/*
 * 読み上げ文設定画面
 * 一覧 → 各設定の編集 の段階的な画面。編集中の画面はクエリパラメータで表す
 */
export function PhraseSetting(): ReactElement {
  return (
    <Suspense fallback={<LoadingOverlay isOpen />}>
      <PhraseSettingContent />
    </Suspense>
  );
}

function PhraseSettingContent(): ReactElement {
  const t = useModifiedTranslation();
  const toast = useToast();
  const toastMessage = useToastMessage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tokenInfo] = useAtom(accessTokenInfoAtom);
  const [{ data }] = useAtom(phraseSettingQueryAtom);
  const allowOverride = data.allow_phrase_override !== false;
  const companySetting = data.default_setting_data ?? data.setting_data;
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingCompanySetting, setIsConfirmingCompanySetting] =
    useState(false);
  const useCompanySetting =
    !allowOverride || (data.is_default_used && !isEditing);
  // 企業の設定値を使用中なら、それを一覧と編集の下敷きにする
  const settingData =
    data.is_default_used || !allowOverride ? companySetting : data.setting_data;
  // 取り直した設定に追従させる。toFormData は id を振り直すので描画ごとには作らない
  const savedFormData = useMemo(() => toFormData(settingData), [settingData]);
  // 並べ替えの保存中だけ、保存前の並びを表示する
  const [formData, setOptimisticFormData] = useOptimistic(savedFormData);

  const showSaved = (): void => {
    toast({
      title: t("PhraseSetting.saved"),
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };

  // 更新後は手元の設定も読み上げ文も信用できないので取り直させる
  const discardCaches = async (): Promise<void> => {
    // 次に録音画面を開いたとき、変更前の設定で選んだ文を使わないよう空にする
    await queryClient.resetQueries({ queryKey: [RECORDING_PHRASES_KEY] });
    await queryClient.invalidateQueries({ queryKey: [PHRASE_SETTING_KEY] });
  };

  const save = async (data: PhraseSettingFormData): Promise<boolean> => {
    if (!tokenInfo) return false;
    try {
      await voislogApi.PUT("/v2/v_management/app_settings/phrase", {
        body: {
          setting_data: toSettingData(data),
          user_id: tokenInfo.user_id,
        },
      });
      showSaved();
      await discardCaches();
      return true;
    } catch (e) {
      toastMessage(e);
      return false;
    }
  };

  // 編集画面から保存したら一覧へ戻る
  const saveAndBack = async (data: PhraseSettingFormData): Promise<void> => {
    if (await save(data)) navigate(-1);
  };

  const onToggleCompanySetting = (checked: boolean): void => {
    if (!checked) {
      // オフにすると企業の設定を写して自分で直せるようにする
      setIsEditing(true);
      return;
    }
    setIsConfirmingCompanySetting(true);
  };

  const deleteOwnSetting = async (): Promise<void> => {
    setIsConfirmingCompanySetting(false);
    if (!tokenInfo) return;
    try {
      await voislogApi.DELETE("/v2/v_management/app_settings/phrase", {
        params: { query: { user_id: tokenInfo.user_id } },
      });
      setIsEditing(false);
      showSaved();
      await discardCaches();
    } catch (e) {
      toastMessage(e);
    }
  };

  const onMove = (from: number, to: number): void => {
    const settings = [...formData.phrases_settings];
    const [moved] = settings.splice(from, 1);
    settings.splice(to, 0, moved);
    const next = { ...formData, phrases_settings: settings };
    startTransition(async () => {
      setOptimisticFormData(next);
      await save(next);
    });
  };

  // 企業の設定値を使用中は編集画面を開かない
  const view = useCompanySetting ? null : searchParams.get("view");
  const indexParam = searchParams.get("index");

  if (view === "setting" && indexParam !== null) {
    const isNew = indexParam === "new";
    const index = isNew ? formData.phrases_settings.length : Number(indexParam);
    return (
      <PhraseSettingEditor
        key={indexParam}
        savedValue={formData.phrases_settings[index]}
        initialValue={
          formData.phrases_settings[index] ?? {
            ...EMPTY_PHRASES_SETTING,
            num_utterance: formData.minimum_utterances,
            phrases: DEFAULT_PHRASE_KEYS.map((key) => ({
              phrase: t(key),
              disp_order: null,
            })),
          }
        }
        minimumUtterances={Number(formData.minimum_utterances)}
        isNew={isNew}
        onSave={(value) => {
          const settings = [...formData.phrases_settings];
          settings[index] = { ...value, id: value.id || createSettingId() };
          return saveAndBack({ ...formData, phrases_settings: settings });
        }}
        onDelete={() =>
          saveAndBack({
            ...formData,
            phrases_settings: formData.phrases_settings.filter(
              (_, i) => i !== index,
            ),
          })
        }
      />
    );
  }

  if (view === "minimum") {
    return (
      <MinimumUtterancesEditor
        initialValue={formData.minimum_utterances}
        savedValue={formData.minimum_utterances}
        numUtterances={formData.phrases_settings.map((s) =>
          Number(s.num_utterance),
        )}
        onSave={(value) =>
          saveAndBack({ ...formData, minimum_utterances: value })
        }
      />
    );
  }

  return (
    <PhraseSettingLayout title={t("Setting.itemTitlePhrase")}>
      <PhraseSettingList
        formData={formData}
        useCompanySetting={useCompanySetting}
        allowOverride={allowOverride}
        onToggleCompanySetting={onToggleCompanySetting}
        onOpenSetting={(index) =>
          setSearchParams({ view: "setting", index: String(index) })
        }
        onOpenMinimum={() => setSearchParams({ view: "minimum" })}
        onMove={onMove}
      />
      <SimpleAlertDialog
        isOpen={isConfirmingCompanySetting}
        okTestId="phrase-setting-use-company-confirm"
        ok={t("Modal.yes")}
        cancel={t("Modal.no")}
        description={t("PhraseSetting.confirmUseCompanySetting")}
        onOk={deleteOwnSetting}
        onClose={() => setIsConfirmingCompanySetting(false)}
      />
    </PhraseSettingLayout>
  );
}
